/**
 * Revenue sharing.
 *
 * Every pound collected is split three ways:
 *
 *   Coach           40%     of the revenue they earned
 *   Manager coach   42.9%   Shehab Khalil, across everyone else's revenue
 *   City View       17.1%   the branch's cut
 *                  ------
 *                   100%
 *
 * When the manager coach earned the revenue himself — he runs Kickboxing
 * Adults, and may run privates too — he takes both shares, 40 + 42.9 = 82.9%,
 * and City View still takes 17.1%. The split always sums to exactly 100%: no pound is
 * ever unallocated, and none is ever paid twice.
 *
 * A NAMED COACH MAY BE ON A DIFFERENT RATE. Coaches are not all hired on the
 * same terms, so `CoachRates` maps a coach's name to their own percentage and
 * the default applies to everyone else. The extra comes out of the MANAGER's
 * share, never out of City View's: the branch's 17.1% is a venue agreement and
 * has nothing to do with which coach happened to take the class. So the coach
 * and manager percentages together form a fixed POOL — 82.9% by default — and an
 * override only moves points inside it. City View's cut is untouched and the
 * three shares still sum to exactly 100%.
 *
 * WHAT EARNS WHAT
 *
 * Revenue is bucketed into "units", not classes, because a class and a private
 * hour in the same class are frequently taught by different people:
 *
 *   group    one unit per class. Earned by the class coach from
 *            `coach_assignments`. Walk-ins land here — a drop-in joins the
 *            class, so the class coach earns it.
 *   private  one unit per (class, coach) pair, earned by the coach named on
 *            the subscription. Two coaches running privates in the same class
 *            are two separate units and two separate payouts.
 *
 * That is why a coach with no class assigned still appears in the report: the
 * roster in Settings names them, and their privates earn independently of any
 * class. Roster coaches with no revenue at all are listed at zero rather than
 * omitted, so "he did no privates this month" is visible instead of looking
 * like the report forgot him.
 *
 * Percentages live in settings rather than in this file, because they will
 * change before the code does. They need not be whole numbers — City View's
 * cut is 17.1% — so every sum and split is done in integer BASIS POINTS
 * (hundredths of a percent). In floating point 40 + 42.9 + 17.1 is not
 * reliably 100, and a "does it add to 100%" check that fails on a correct
 * split would lock the Settings save button.
 *
 * Rounding: shares are computed with integer maths and the LARGEST share
 * absorbs the remainder, so the parts always add back to the total. Paying out
 * 9,999.99 of a 10,000 class because three divisions each rounded down is the
 * kind of error that costs an afternoon to find.
 */
import type { ISODate, Payment, Subscription, WalkIn } from './types';
import { classLabel } from './catalog';
import { isWithin } from './dates';

/** The default split, seeded into settings on first run. */
export const DEFAULT_SPLIT = {
  coachPercent: 40,
  managerPercent: 42.9,
  cityViewPercent: 17.1,
} as const;

/** A percentage as integer hundredths: 17.1% -> 1710. The finest step a split can take. */
export function toBasisPoints(percent: number): number {
  return Math.round(percent * 100);
}

/** Back to a percentage, without float noise such as 32.900000000000006. */
export function fromBasisPoints(bp: number): number {
  return bp / 100;
}

/** Snap a typed percentage to the precision the split works at. */
export function roundPercent(percent: number): number {
  return fromBasisPoints(toBasisPoints(percent));
}

/** Percentages summed exactly, e.g. for the "adds up to 100%" check. */
export function sumPercents(...percents: number[]): number {
  return fromBasisPoints(percents.reduce((sum, p) => sum + toBasisPoints(p), 0));
}

/** True when the three shares allocate every pound — exactly 100%, not 99.999…%. */
export function isBalancedSplit(split: SplitConfig): boolean {
  return (
    toBasisPoints(split.coachPercent) +
      toBasisPoints(split.managerPercent) +
      toBasisPoints(split.cityViewPercent) ===
    10_000
  );
}

/**
 * Coaches hired on their own terms, seeded on first run.
 *
 * This is a SEED, not the truth — the live map lives in `app_settings` under
 * `coach_rates` and is edited in Settings → Coach rates. Editing a rate here
 * afterwards changes nothing on a database that has already seeded, which is
 * deliberate: a deploy must not be able to silently re-rate someone's pay.
 *
 * Matched case-insensitively against the roster, so the spelling only has to be
 * close. If a name here matches nobody, that coach is simply on the default —
 * Settings shows every roster name beside its rate, which is where a mismatch
 * becomes visible.
 *
 * Empty: every coach currently earns the default, and rates are set by the
 * manager in Settings. A name seeded here would reach the cloud from any
 * freshly installed device and could re-rate someone the manager had already
 * moved back to the default.
 */
export const DEFAULT_COACH_RATES: CoachRates = {};

export const MANAGER_COACH_NAME = 'Shehab Khalil';

/** The class the manager coach teaches himself, where he takes 40 + 42.9 = 82.9%. */
export const MANAGER_COACH_CLASS = 'kickboxing_adults';

export interface SplitConfig {
  coachPercent: number;
  managerPercent: number;
  cityViewPercent: number;
}

/** class_key -> coach name. Unassigned classes fall back to "Unassigned". */
export type CoachAssignments = Record<string, string>;

/**
 * coach name -> that coach's own percentage. A coach who is not in here is on
 * `split.coachPercent`, which is the normal case — this map holds exceptions
 * only, so the default rate stays editable in one place.
 *
 * Keyed by NAME for the same reason `private_coach` is: the roster lives in two
 * tables (class coaches and private-only coaches) and a name is the only thing
 * both share.
 */
export type CoachRates = Record<string, number>;

/**
 * Names are matched case- and whitespace-insensitively.
 *
 * Staff type coach names by hand in two different screens, so "beisy " and
 * "Beisy" have to be the same person — an override that silently stopped
 * applying because of a stray capital would underpay someone for a month
 * before anyone noticed.
 */
function rateKey(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * Read the rates map out of the synced settings blob.
 *
 * Deliberately total: anything unparseable yields an empty map, which means
 * "everyone on the default rate". A corrupt setting must not be able to stop
 * the payout report rendering — the report is how people get paid.
 */
export function parseCoachRates(raw: string | null | undefined): CoachRates {
  if (!raw) return {};

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};

  const out: CoachRates = {};
  for (const [name, value] of Object.entries(parsed as Record<string, unknown>)) {
    const pct = typeof value === 'number' ? value : Number(value);
    // 0 is a legitimate rate; NaN and out-of-range are not.
    if (!Number.isFinite(pct) || pct < 0 || pct > 100 || !name.trim()) continue;
    out[name.trim()] = pct;
  }
  return out;
}

export function serialiseCoachRates(rates: CoachRates): string {
  return JSON.stringify(rates);
}

/**
 * Set one coach's rate, replacing any entry that names the same person.
 *
 * Lookups are case-insensitive, so writing "Beisy" while "beisy" is already in
 * the map would leave TWO entries for one coach and the reader would take
 * whichever came first. Editing a rate in Settings and seeing it not take
 * effect is exactly the sort of bug that gets found on payday.
 */
export function withCoachRate(rates: CoachRates, name: string, percent: number): CoachRates {
  const wanted = rateKey(name);
  const out: CoachRates = {};
  for (const [existing, pct] of Object.entries(rates)) {
    if (rateKey(existing) !== wanted) out[existing] = pct;
  }
  out[name.trim()] = percent;
  return out;
}

/**
 * Keep only the coaches who differ from the default.
 *
 * A rate stored as equal to the default would silently PIN that coach if the
 * default ever moved — they would stay on 40% while everyone else went to 45%,
 * and nothing on screen would explain why.
 */
export function exceptionsOnly(rates: CoachRates, defaultPercent: number): CoachRates {
  const out: CoachRates = {};
  for (const [name, pct] of Object.entries(rates)) {
    if (!name.trim() || !Number.isFinite(pct) || pct === defaultPercent) continue;
    out[name.trim()] = pct;
  }
  return out;
}

/**
 * The coach and manager percentages together. An override moves points between
 * these two and never touches City View.
 */
export function coachManagerPool(split: SplitConfig): number {
  return sumPercents(split.coachPercent, split.managerPercent);
}

/**
 * What this coach takes, and what the manager takes off them.
 *
 * Clamped to the pool so an override can never invent money: setting someone to
 * 95% of an 82.9% pool pays them 82.9% and the manager nothing, rather than
 * producing shares that add to 110%.
 */
export function ratesFor(
  coach: string,
  split: SplitConfig,
  rates: CoachRates = {}
): { coachPercent: number; managerPercent: number } {
  const override = lookupRate(coach, rates);

  if (override === undefined) {
    return { coachPercent: split.coachPercent, managerPercent: split.managerPercent };
  }

  const poolBp = Math.max(0, toBasisPoints(coachManagerPool(split)));
  const coachBp = Math.min(Math.max(0, toBasisPoints(override)), poolBp);
  return {
    coachPercent: fromBasisPoints(coachBp),
    managerPercent: fromBasisPoints(poolBp - coachBp),
  };
}

/**
 * A linear scan rather than a keyed lookup, so the map works whether its keys
 * were written by this code or typed by a manager in Settings. There are a
 * dozen coaches at most and this runs once per earning unit.
 */
function lookupRate(coach: string, rates: CoachRates): number | undefined {
  const wanted = rateKey(coach);
  if (!wanted) return undefined;
  for (const [name, pct] of Object.entries(rates)) {
    if (rateKey(name) === wanted) return Number.isFinite(pct) ? pct : undefined;
  }
  return undefined;
}

export const UNASSIGNED_COACH = 'Unassigned';

/**
 * A private subscription with nobody named on it. Older rows predate the coach
 * dropdown, so their money has to go somewhere visible rather than silently
 * joining the class coach's total.
 */
export const UNASSIGNED_PRIVATE_COACH = 'Unassigned (private)';

/**
 * One earning unit: a class taught as a group, or one coach's privates within
 * a class. This is the row the payout tables render.
 */
export interface ClassRevenueRow {
  /** Stable identity for React keys and filtering: `class_key` or `class_key::private::coach`. */
  unitKey: string;
  classKey: string;
  /** True when this row is private one-to-one revenue. */
  private: boolean;
  /** "Kickboxing (Adults)" or "Kickboxing (Adults) · Private". */
  label: string;
  /** Money collected for this unit in the period. */
  revenue: number;
  /** Distinct paying members in the period. */
  members: number;
  walkIns: number;
  coach: string;
  /** True when this unit's coach is also the manager coach. */
  coachIsManager: boolean;
  coachShare: number;
  managerShare: number;
  cityViewShare: number;
  /**
   * The percentages this row was actually computed with. Rendered rather than
   * the configured default, because a coach on an override earns a different
   * one and a table that says 40% next to a 50% figure is how a payout report
   * loses its credibility.
   */
  coachPercent: number;
  managerPercent: number;
}

export interface CoachPayout {
  coach: string;
  isManager: boolean;
  /** The units they earn from, and what each contributed. */
  classes: {
    unitKey: string;
    classKey: string;
    label: string;
    private: boolean;
    revenue: number;
    share: number;
  }[];
  /**
   * Everything taken across their units — before anyone's percentage.
   * This is the "made from his classes, walk-ins and privates" figure.
   */
  grossRevenue: number;
  /** Their own cut of `grossRevenue` (40%, or 82.9% for the manager coach). */
  teachingShare: number;
  /**
   * The percentage behind `teachingShare` — the default, this coach's own
   * override, or the whole coach+manager pool for the manager coach.
   */
  coachPercent: number;
  /** True when this coach is on a rate of their own rather than the default. */
  hasOwnRate: boolean;
  /** What the manager takes off THIS coach's gross. Zero when they are the manager. */
  managerCut: number;
  /** What City View takes off this coach's gross. */
  cityViewCut: number;
  /** Earnings from the manager override across everyone else (Shehab only). */
  managerShare: number;
  /** teachingShare + managerShare — what this person is actually paid. */
  total: number;
}

export interface PayoutReport {
  totalRevenue: number;
  byClass: ClassRevenueRow[];
  coaches: CoachPayout[];
  /**
   * The coach share paid to everyone EXCEPT the manager coach — the 40% the
   * coaches took. The manager's own earnings are `managerTotal`, so the two
   * never double-count.
   */
  coachesTotal: number;
  /** The manager coach's whole take: 42.9% of others, plus 82.9% of his own units. */
  managerTotal: number;
  cityViewTotal: number;
  /** Should always be 0. Non-zero means the split does not sum to 100%. */
  unallocated: number;
  split: SplitConfig;
  /** The per-coach overrides this report was run with. */
  coachRates: CoachRates;
}

/**
 * Split an integer amount by percentages without losing or inventing piastres.
 * The largest share takes the rounding remainder.
 *
 * Works in basis points so 17.1% is the integer 1710, not a float: piastres ×
 * basis points stays an exact integer far beyond any till this will ever see.
 */
export function splitAmount(total: number, percents: number[]): number[] {
  if (percents.length === 0) return [];

  const shares = percents.map((p) => Math.floor((total * toBasisPoints(p)) / 10_000));
  const distributed = shares.reduce((a, b) => a + b, 0);
  const remainder = total - distributed;

  if (remainder !== 0) {
    // Give the leftover to whoever has the biggest slice.
    let biggest = 0;
    for (let i = 1; i < percents.length; i++) {
      if ((percents[i] ?? 0) > (percents[biggest] ?? 0)) biggest = i;
    }
    shares[biggest] = (shares[biggest] ?? 0) + remainder;
  }

  return shares;
}

/** Who earns a subscription's revenue, and under which unit. */
function unitForSubscription(
  sub: Subscription,
  coachAssignments: CoachAssignments
): { unitKey: string; classKey: string; private: boolean; coach: string } {
  if (sub.private) {
    const coach = sub.private_coach?.trim() || UNASSIGNED_PRIVATE_COACH;
    return {
      unitKey: `${sub.class_key}::private::${coach}`,
      classKey: sub.class_key,
      private: true,
      coach,
    };
  }
  return {
    unitKey: sub.class_key,
    classKey: sub.class_key,
    private: false,
    coach: coachAssignments[sub.class_key] ?? UNASSIGNED_COACH,
  };
}

interface UnitData {
  unitKey: string;
  classKey: string;
  private: boolean;
  coach: string;
  revenue: number;
  members: Set<string>;
  walkIns: number;
}

/**
 * Revenue collected per earning unit in a period.
 *
 * A payment is attributed to the unit of the subscription it settles — so
 * someone clearing an old judo balance credits judo, not whatever they train
 * now, and a private payment credits the private coach rather than the class.
 * Walk-ins credit the class attended, as group revenue.
 *
 * Negative payments (refunds from a cancellation) flow through untouched, which
 * is what makes a refund reduce the right coach's payout automatically.
 */
export function revenuePerClass(
  payments: readonly Payment[],
  subscriptions: readonly Subscription[],
  walkIns: readonly WalkIn[],
  range: { from: ISODate; to: ISODate },
  coachAssignments: CoachAssignments = {}
): Map<string, UnitData> {
  const subUnit = new Map(
    subscriptions.map((s) => [s.id, unitForSubscription(s, coachAssignments)])
  );
  const out = new Map<string, UnitData>();

  const bucket = (unit: {
    unitKey: string;
    classKey: string;
    private: boolean;
    coach: string;
  }) => {
    let entry = out.get(unit.unitKey);
    if (!entry) {
      entry = {
        ...unit,
        revenue: 0,
        members: new Set<string>(),
        walkIns: 0,
      };
      out.set(unit.unitKey, entry);
    }
    return entry;
  };

  for (const p of payments) {
    if (p.deleted_at !== null || !isWithin(p.paid_at, range.from, range.to)) continue;
    const unit = subUnit.get(p.subscription_id);
    if (!unit) continue; // Unknown subscription — never guess where money came from.
    const entry = bucket(unit);
    entry.revenue += p.amount_piastres;
    entry.members.add(p.member_id);
  }

  for (const w of walkIns) {
    if (w.deleted_at !== null || !isWithin(w.visited_at, range.from, range.to)) continue;
    // A drop-in joins the class, so it is group revenue for the class coach.
    const entry = bucket({
      unitKey: w.class_key,
      classKey: w.class_key,
      private: false,
      coach: coachAssignments[w.class_key] ?? UNASSIGNED_COACH,
    });
    entry.revenue += w.amount_piastres;
    entry.walkIns++;
  }

  return out;
}

/** "Kickboxing (Adults)" or "Kickboxing (Adults) · Private". */
export function unitLabel(classKey: string, isPrivate: boolean): string {
  return isPrivate ? `${classLabel(classKey)} · Private` : classLabel(classKey);
}

/**
 * The full payout report for a period.
 *
 * `coachAssignments` maps class_key -> coach name; `roster` is every coach
 * named in Settings, including those with no class. A class with no coach
 * assigned still has its 40% computed and attributed to "Unassigned", so the
 * money is visible rather than silently vanishing from the totals.
 *
 * `coachRates` names the coaches who are not on the default percentage. Their
 * extra comes out of the manager's share, so City View's 17.1% is identical
 * whoever taught the class.
 */
export function computePayouts(
  payments: readonly Payment[],
  subscriptions: readonly Subscription[],
  walkIns: readonly WalkIn[],
  range: { from: ISODate; to: ISODate },
  coachAssignments: CoachAssignments,
  split: SplitConfig = DEFAULT_SPLIT,
  managerCoach: string = MANAGER_COACH_NAME,
  roster: readonly string[] = [],
  coachRates: CoachRates = {}
): PayoutReport {
  const perUnit = revenuePerClass(
    payments,
    subscriptions,
    walkIns,
    range,
    coachAssignments
  );

  const byClass: ClassRevenueRow[] = [];
  let totalRevenue = 0;
  let cityViewTotal = 0;

  // coach name -> accumulating payout
  const coachMap = new Map<string, CoachPayout>();
  const coachEntry = (name: string): CoachPayout => {
    let entry = coachMap.get(name);
    if (!entry) {
      const isManager = name === managerCoach;
      const own = ratesFor(name, split, coachRates);
      entry = {
        coach: name,
        isManager,
        classes: [],
        grossRevenue: 0,
        teachingShare: 0,
        // The manager takes the whole pool on his own units; an override on
        // his name would be meaningless, so it is deliberately not consulted.
        coachPercent: isManager ? coachManagerPool(split) : own.coachPercent,
        hasOwnRate: !isManager && own.coachPercent !== split.coachPercent,
        managerCut: 0,
        cityViewCut: 0,
        managerShare: 0,
        total: 0,
      };
      coachMap.set(name, entry);
    }
    return entry;
  };

  // Everyone named in Settings appears, even at zero. A coach who ran no
  // privates this month should read as "earned nothing", not be missing.
  for (const name of roster) {
    if (name.trim()) coachEntry(name.trim());
  }
  for (const name of Object.values(coachAssignments)) {
    if (name.trim()) coachEntry(name.trim());
  }

  for (const unit of perUnit.values()) {
    const coachIsManager = unit.coach === managerCoach;

    let coachShare: number;
    let managerShare: number;
    let cityViewShare: number;
    let coachPercent: number;
    let managerPercent: number;

    if (coachIsManager) {
      // He is both the coach and the manager: 40 + 42.9 = 82.9%, in one share, so
      // the rounding remainder cannot be split against him.
      coachPercent = coachManagerPool(split);
      managerPercent = 0;
      [coachShare, cityViewShare] = splitAmount(unit.revenue, [
        coachPercent,
        split.cityViewPercent,
      ]) as [number, number];
      managerShare = 0;
    } else {
      // An override moves points from the manager to this coach. City View's
      // percentage is the same on every unit, whoever taught it.
      ({ coachPercent, managerPercent } = ratesFor(unit.coach, split, coachRates));
      [coachShare, managerShare, cityViewShare] = splitAmount(unit.revenue, [
        coachPercent,
        managerPercent,
        split.cityViewPercent,
      ]) as [number, number, number];
    }

    totalRevenue += unit.revenue;
    cityViewTotal += cityViewShare;

    const label = unitLabel(unit.classKey, unit.private);

    byClass.push({
      unitKey: unit.unitKey,
      classKey: unit.classKey,
      private: unit.private,
      label,
      revenue: unit.revenue,
      members: unit.members.size,
      walkIns: unit.walkIns,
      coach: unit.coach,
      coachIsManager,
      coachShare,
      managerShare,
      cityViewShare,
      coachPercent,
      managerPercent,
    });

    const entry = coachEntry(unit.coach);
    entry.grossRevenue += unit.revenue;
    entry.teachingShare += coachShare;
    entry.managerCut += managerShare;
    entry.cityViewCut += cityViewShare;
    entry.classes.push({
      unitKey: unit.unitKey,
      classKey: unit.classKey,
      label,
      private: unit.private,
      revenue: unit.revenue,
      share: coachShare,
    });

    if (managerShare > 0) {
      const manager = coachEntry(managerCoach);
      manager.managerShare += managerShare;
    }
  }

  for (const entry of coachMap.values()) {
    entry.total = entry.teachingShare + entry.managerShare;
    entry.classes.sort((a, b) => b.revenue - a.revenue);
  }

  const coaches = [...coachMap.values()].sort(
    (a, b) => b.total - a.total || a.coach.localeCompare(b.coach)
  );

  const managerTotal = coaches.find((c) => c.isManager)?.total ?? 0;
  const coachesTotal = coaches
    .filter((c) => !c.isManager)
    .reduce((sum, c) => sum + c.teachingShare, 0);

  byClass.sort((a, b) => b.revenue - a.revenue);

  return {
    totalRevenue,
    byClass,
    coaches,
    coachesTotal,
    managerTotal,
    cityViewTotal,
    unallocated: totalRevenue - (coachesTotal + managerTotal + cityViewTotal),
    split,
    coachRates,
  };
}

/** Seeded coach assignments. Only Shehab's own class is known up front. */
export function defaultCoachAssignments(): CoachAssignments {
  return { [MANAGER_COACH_CLASS]: MANAGER_COACH_NAME };
}
